<template>
  <v-container fluid class="pa-0 pa-md-2">
    <v-card color="grey darken-4" dark>
      <v-card-title class="d-flex align-center flex-wrap text-subtitle-1 text-sm-h6 pa-2 pa-sm-4">
        <v-icon left>mdi-helicopter</v-icon>
        Flugverkehr
        <v-chip v-if="ageLabel" :color="ageColor" x-small class="ml-2" text-color="white">{{ ageLabel }}</v-chip>
        <v-spacer />
        <v-btn icon small :loading="loading" @click="load"><v-icon small>mdi-refresh</v-icon></v-btn>
      </v-card-title>
      <v-card-subtitle class="pb-2">
        Flugrouten der getrackten Hubschrauber: BPO (Bundespolizei, blau) und CHX (Christoph/DRF, orange).
      </v-card-subtitle>
      <v-divider />
      <v-card-text class="pa-2 pa-md-3">
        <div
          v-if="imgUrl"
          ref="viewport"
          class="map-viewport"
          @wheel.prevent="onWheel"
          @pointerdown="onPointerDown"
          @pointermove="onPointerMove"
          @pointerup="onPointerUp"
          @pointercancel="onPointerUp"
          @dblclick="reset"
        >
          <img
            :src="imgUrl"
            class="map-img"
            :class="{ 'no-anim': dragging }"
            :style="imgStyle"
            draggable="false"
            alt="ADSB-Karte"
          />
          <div class="map-controls">
            <v-btn icon small color="white" class="map-ctrl-btn" @click="zoomBy(1.3)"><v-icon>mdi-plus</v-icon></v-btn>
            <v-btn icon small color="white" class="map-ctrl-btn" @click="zoomBy(1/1.3)"><v-icon>mdi-minus</v-icon></v-btn>
            <v-btn icon small color="white" class="map-ctrl-btn" @click="reset"><v-icon>mdi-fit-to-page-outline</v-icon></v-btn>
          </div>
        </div>
        <div v-else-if="error" class="grey--text py-8 text-center">{{ error }}</div>
        <div v-else class="grey--text py-8 text-center">
          <v-progress-circular indeterminate size="32" />
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
const BASE_URL = '/api'
const POLL_MS = 30 * 60 * 1000 // Karte wird nur einmal täglich erzeugt → halbstündlich prüfen reicht
const STALE_AFTER_S = 26 * 3600 // bis 26 h gilt die Karte als aktuell (Puffer über dem Tagesrhythmus)
const MIN_SCALE = 1
const MAX_SCALE = 8

export default {
  name: 'AdsbMap',
  data: () => ({
    imgUrl: null,
    ageSeconds: null,
    error: '',
    loading: false,
    timer: null,
    // Pan/Zoom-State
    scale: 1,
    tx: 0,
    ty: 0,
    dragging: false,
    pointers: new Map(),
    pinchPrevDist: 0,
  }),
  computed: {
    ageLabel() {
      if (this.ageSeconds === null) return ''
      const s = this.ageSeconds
      if (s < 60) return `vor ${s} s`
      if (s < 3600) return `vor ${Math.floor(s / 60)} min`
      if (s < 48 * 3600) return `vor ${Math.floor(s / 3600)} h`
      return `vor ${Math.floor(s / 86400)} d`
    },
    ageColor() {
      if (this.ageSeconds === null) return 'grey darken-1'
      return this.ageSeconds > STALE_AFTER_S ? 'orange darken-2' : 'green darken-2'
    },
    imgStyle() {
      return {
        transform: `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`,
        transformOrigin: '0 0',
      }
    },
  },
  mounted() {
    this.load()
    this.timer = setInterval(this.load, POLL_MS)
  },
  beforeDestroy() {
    clearInterval(this.timer)
    this.revoke()
  },
  methods: {
    revoke() {
      if (this.imgUrl) {
        URL.revokeObjectURL(this.imgUrl)
        this.imgUrl = null
      }
    },
    async load() {
      this.loading = true
      try {
        const res = await fetch(BASE_URL + '/adsb/map.png', { cache: 'no-store' })
        if (res.status === 404) {
          this.revoke()
          this.error = 'Noch keine Karte erzeugt (aktuell keine passenden Hubschrauber).'
          this.ageSeconds = null
          return
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const age = res.headers.get('X-Map-Age-Seconds')
        this.ageSeconds = age !== null ? parseInt(age, 10) : null
        const blob = await res.blob()
        this.revoke()
        this.imgUrl = URL.createObjectURL(blob)
        this.error = ''
      } catch (e) {
        this.error = 'Karte konnte nicht geladen werden: ' + ((e && e.message) || e)
      } finally {
        this.loading = false
      }
    },
    clampScale(s) {
      return Math.min(Math.max(s, MIN_SCALE), MAX_SCALE)
    },
    // Zoomt um einen Punkt (px,py) relativ zum Viewport, sodass er unter dem Cursor bleibt.
    zoomAt(px, py, factor) {
      const newScale = this.clampScale(this.scale * factor)
      const ratio = newScale / this.scale
      this.tx = px - (px - this.tx) * ratio
      this.ty = py - (py - this.ty) * ratio
      this.scale = newScale
      if (this.scale === MIN_SCALE) { this.tx = 0; this.ty = 0 }
    },
    onWheel(e) {
      const rect = this.$refs.viewport.getBoundingClientRect()
      this.zoomAt(e.clientX - rect.left, e.clientY - rect.top, e.deltaY < 0 ? 1.15 : 1 / 1.15)
    },
    zoomBy(factor) {
      const rect = this.$refs.viewport.getBoundingClientRect()
      this.zoomAt(rect.width / 2, rect.height / 2, factor)
    },
    reset() {
      this.scale = 1
      this.tx = 0
      this.ty = 0
    },
    onPointerDown(e) {
      this.$refs.viewport.setPointerCapture(e.pointerId)
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY })
      this.dragging = true
      if (this.pointers.size === 2) this.pinchPrevDist = this.pointerDistance()
    },
    onPointerMove(e) {
      if (!this.pointers.has(e.pointerId)) return
      const prev = this.pointers.get(e.pointerId)
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY })

      if (this.pointers.size === 1) {
        // Pan
        this.tx += e.clientX - prev.x
        this.ty += e.clientY - prev.y
      } else if (this.pointers.size === 2) {
        // Pinch-Zoom um den Mittelpunkt der zwei Finger
        const dist = this.pointerDistance()
        if (this.pinchPrevDist > 0) {
          const rect = this.$refs.viewport.getBoundingClientRect()
          const mid = this.pointerMidpoint()
          this.zoomAt(mid.x - rect.left, mid.y - rect.top, dist / this.pinchPrevDist)
        }
        this.pinchPrevDist = dist
      }
    },
    onPointerUp(e) {
      this.pointers.delete(e.pointerId)
      if (this.pointers.size < 2) this.pinchPrevDist = 0
      if (this.pointers.size === 0) this.dragging = false
    },
    pointerDistance() {
      const [a, b] = [...this.pointers.values()]
      return Math.hypot(a.x - b.x, a.y - b.y)
    },
    pointerMidpoint() {
      const [a, b] = [...this.pointers.values()]
      return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
    },
  },
}
</script>

<style scoped>
.map-viewport {
  position: relative;
  overflow: hidden;
  width: 100%;
  max-height: 75vh;
  aspect-ratio: 4 / 3;
  background: #111;
  border-radius: 4px;
  cursor: grab;
  touch-action: none;
}
.map-viewport:active { cursor: grabbing; }
.map-img {
  display: block;
  width: 100%;
  user-select: none;
  -webkit-user-drag: none;
  will-change: transform;
  transition: transform 80ms ease-out;
}
.map-img.no-anim { transition: none; }
.map-controls {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  flex-direction: column;
}
.map-ctrl-btn {
  background: rgba(0, 0, 0, 0.55);
  margin-bottom: 6px;
}
</style>
